import { useMemo, useState } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { CrawlUrlDepthControls } from '../../features/crawlUrlTree/CrawlUrlDepthControls';
import { CrawlUrlTreeRow } from '../../features/crawlUrlTree/CrawlUrlTreeRow';
import {
  buildCrawlUrlTree,
  filterRealCrawlNodeIds,
  type CrawlUrlTreeNode,
} from '../../features/crawlUrlTree/buildCrawlUrlTree';
import {
  collectExpandableNodeIds,
  collectNodeIdsAtTreeDepth,
  collectRealDescendantIds,
  MIN_DISPLAY_DEPTH,
} from '../../features/crawlUrlTree/crawlUrlTreeHelpers';
import { useI18n } from '../../i18n/useI18n';
import type { CrawlUrlNode, WithResourceId } from '../../types/knowledge';
import './CrawlUrlSelectionModal.css';

type LevelInfoKind = 'collapse' | 'expand';

type CrawlUrlSelectionModalProps = {
  startUrl: string;
  nodes: Array<WithResourceId<CrawlUrlNode>>;
  selectedNodeIds: string[];
  onSelectedNodeIdsChange: (selectedNodeIds: string[]) => void;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel: string;
  description?: string;
  isSubmitting?: boolean;
  isLoading?: boolean;
};

export function CrawlUrlSelectionModal({
  startUrl,
  nodes,
  selectedNodeIds,
  onSelectedNodeIdsChange,
  onConfirm,
  onClose,
  confirmLabel,
  description,
  isSubmitting = false,
  isLoading = false,
}: CrawlUrlSelectionModalProps) {
  const { translate } = useI18n();
  const tree = useMemo(() => buildCrawlUrlTree(nodes), [nodes]);
  const selectedIdSet = useMemo(
    () => new Set(selectedNodeIds),
    [selectedNodeIds],
  );
  const realSelectedCount = filterRealCrawlNodeIds(selectedNodeIds).length;
  const defaultExpandedNodeIds = useMemo(
    () => collectExpandableNodeIds(tree),
    [tree],
  );

  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(
    () => new Set(defaultExpandedNodeIds),
  );
  const [expandedForTree, setExpandedForTree] = useState(tree);
  const [collapseDisplayDepth, setCollapseDisplayDepth] =
    useState(MIN_DISPLAY_DEPTH);
  const [expandDisplayDepth, setExpandDisplayDepth] =
    useState(MIN_DISPLAY_DEPTH);
  const [levelInfo, setLevelInfo] = useState<LevelInfoKind | null>(null);

  if (tree !== expandedForTree) {
    setExpandedForTree(tree);
    setExpandedNodeIds(new Set(defaultExpandedNodeIds));
  }

  const handleToggleSelection = (node: CrawlUrlTreeNode, selected: boolean) => {
    // Only persist backend node ids — synthetic parents are UI-only.
    const affectedIds = collectRealDescendantIds(node);
    if (affectedIds.length === 0) {
      return;
    }

    if (selected) {
      onSelectedNodeIdsChange([
        ...new Set([...selectedNodeIds, ...affectedIds]),
      ]);
      return;
    }

    const removeIds = new Set(affectedIds);
    onSelectedNodeIdsChange(selectedNodeIds.filter((id) => !removeIds.has(id)));
  };

  const handleToggleExpanded = (nodeId: string) => {
    setExpandedNodeIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const applyDepthExpansion = (displayDepth: number, expand: boolean) => {
    const treeDepth = displayDepth - 1;
    if (!Number.isInteger(treeDepth) || treeDepth < 0) {
      return;
    }

    const nodeIdsAtDepth = collectNodeIdsAtTreeDepth(tree, treeDepth);
    if (nodeIdsAtDepth.length === 0) {
      return;
    }

    setExpandedNodeIds((current) => {
      const next = new Set(current);
      for (const nodeId of nodeIdsAtDepth) {
        if (expand) {
          next.add(nodeId);
        } else {
          next.delete(nodeId);
        }
      }
      return next;
    });
  };

  const levelInfoContent =
    levelInfo === 'collapse'
      ? {
          title: translate('knowledge.crawlSelectionCollapseLevel'),
          description: translate(
            'knowledge.crawlSelectionCollapseLevelTooltip',
          ),
        }
      : levelInfo === 'expand'
        ? {
            title: translate('knowledge.crawlSelectionExpandLevel'),
            description: translate(
              'knowledge.crawlSelectionExpandLevelTooltip',
            ),
          }
        : null;

  return (
    <>
      <LiksDialog
        isOpen
        size="full"
        className="crawl-url-selection"
        bodyClassName="crawl-url-selection__body"
        footerClassName="crawl-url-selection__footer"
        title={
          <>
            <span className="crawl-url-selection__header-label">
              {translate('knowledge.crawlSelectionTitle')}
            </span>{' '}
            <span className="crawl-url-selection__header-url">{startUrl}</span>
            {!isLoading && nodes.length > 0 && (
              <>
                {' '}
                <span className="crawl-url-selection__header-node-count">
                  {translate('knowledge.crawlSelectionNodeCount', {
                    count: String(nodes.length),
                  })}
                </span>
              </>
            )}
          </>
        }
        closeLabel={translate('knowledge.form.close')}
        onClose={onClose}
        isDismissDisabled={isSubmitting}
        closeDisabled={isSubmitting}
        footer={
          <>
            <CrawlUrlDepthControls
              collapseDisplayDepth={collapseDisplayDepth}
              expandDisplayDepth={expandDisplayDepth}
              disabled={isSubmitting}
              onCollapseDisplayDepthChange={setCollapseDisplayDepth}
              onExpandDisplayDepthChange={setExpandDisplayDepth}
              onApplyCollapseDepth={() =>
                applyDepthExpansion(collapseDisplayDepth, false)
              }
              onApplyExpandDepth={() =>
                applyDepthExpansion(expandDisplayDepth, true)
              }
              onShowCollapseInfo={() => setLevelInfo('collapse')}
              onShowExpandInfo={() => setLevelInfo('expand')}
            />
            <div className="crawl-url-selection__actions">
              <LiksButton
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {translate('knowledge.form.cancel')}
              </LiksButton>
              <LiksButton
                type="button"
                onClick={onConfirm}
                disabled={isSubmitting || realSelectedCount === 0}
              >
                {confirmLabel}
              </LiksButton>
            </div>
          </>
        }
      >
        {description ? (
          <p className="crawl-url-selection__description">{description}</p>
        ) : null}
        <div className="crawl-url-selection__tree-area">
          {isLoading ? (
            <p
              className="crawl-url-selection__loading"
              role="status"
              aria-live="polite"
            >
              <span>{translate('knowledge.crawlSelectionLoading')}</span>
              <span
                className="crawl-url-selection__spinner"
                aria-hidden="true"
              />
            </p>
          ) : tree.length === 0 ? (
            <p className="crawl-url-selection__empty">
              {translate('knowledge.crawlSelectionEmpty')}
            </p>
          ) : (
            <ul className="crawl-url-selection__tree">
              {tree.map((node) => (
                <CrawlUrlTreeRow
                  key={node.id}
                  node={node}
                  selectedNodeIds={selectedIdSet}
                  expandedNodeIds={expandedNodeIds}
                  onToggleSelection={handleToggleSelection}
                  onToggleExpanded={handleToggleExpanded}
                />
              ))}
            </ul>
          )}
        </div>
      </LiksDialog>
      {levelInfoContent ? (
        <LiksDialog
          isOpen
          size="sm"
          title={levelInfoContent.title}
          onClose={() => setLevelInfo(null)}
          closeLabel={translate('knowledge.form.close')}
          footer={
            <LiksButton type="button" onClick={() => setLevelInfo(null)}>
              {translate('knowledge.form.close')}
            </LiksButton>
          }
        >
          <p className="crawl-url-selection__info-description">
            {levelInfoContent.description}
          </p>
        </LiksDialog>
      ) : null}
    </>
  );
}
